/**
 * PAS Features — feature sub-nav + ambient video playback.
 *
 * The big-media column is a scroll-snap carousel: vertical on desktop,
 * horizontal on mobile (≤749px). The Features section shows one big image
 * (or video) per feature. Two controls
 * switch the active feature, and both do the same thing:
 *   - the text heading (.pas-spec-tabs__feature-tab), and
 *   - the thumbnail in the rail next to the image (.pas-spec-tabs__feature-thumb).
 * Each carries data-pas-feature-index; clicking either swaps the visible slide
 * (.pas-spec-tabs__feature-slide), the description, and the active state of both
 * controls.
 *
 * Feature videos render `autoplay muted loop` (no controls) via the `video`
 * snippet, but native autoplay isn't reliable for slides that start hidden or
 * sit below the fold. So playback is driven here, mirroring pas-hero-video.js:
 * the active feature's <video> plays only while the section is in view, forced
 * muted + playsinline so there's never any sound; everything else is paused.
 *
 * Plain delegated listener + one IntersectionObserver per panel. No deps.
 */

(function () {
  if (window.__pasSpecTabsWired) return;
  window.__pasSpecTabsWired = true;

  const IN_VIEW_THRESHOLD = 0.25;

  // --- Feature switching -----------------------------------------------------

  document.addEventListener('click', (event) => {
    const trigger = event.target.closest(
      '.pas-spec-tabs__feature-tab, .pas-spec-tabs__feature-thumb'
    );
    if (trigger) activateFeature(trigger);

    // Prev/next arrows step through features (same look as the hero carousel).
    const arrow = event.target.closest('[data-pas-feature-prev], [data-pas-feature-next]');
    if (arrow) {
      const panel = arrow.closest('.pas-spec-tabs__panel-features');
      if (!panel) return;
      const count = panel.querySelectorAll('[data-pas-feature-media]').length;
      const delta = arrow.hasAttribute('data-pas-feature-prev') ? -1 : 1;
      const idx = Math.min(count - 1, Math.max(0, Number(panel.__pasActiveIdx || 0) + delta));
      goToFeature(panel, idx);
    }
  });

  function activateFeature(trigger) {
    const panel = trigger.closest('.pas-spec-tabs__panel-features');
    if (!panel) return;
    const idx = trigger.getAttribute('data-pas-feature-index');
    if (idx == null) return;
    goToFeature(panel, idx);
  }

  function goToFeature(panel, idx) {
    syncActive(panel, idx);

    // Scroll the media carousel to the chosen feature's slide. Suppress the
    // scroll-driven sync while the smooth scroll passes intermediate slides.
    const slides = panel.querySelector('.pas-spec-tabs__feature-slides');
    if (slides) {
      panel.__pasIgnoreScroll = true;
      const release = () => { panel.__pasIgnoreScroll = false; };
      if ('onscrollend' in slides) slides.addEventListener('scrollend', release, { once: true });
      setTimeout(release, 900);
      const slide = slides.querySelector('[data-pas-feature-media="' + idx + '"]');
      slides.scrollTo({
        top: slide ? slide.offsetTop : Number(idx) * slides.clientHeight,
        left: slide ? slide.offsetLeft : 0,
        behavior: 'smooth',
      });
    }
  }

  // Reflect the active feature across both controls, the description, and
  // video playback. Called from clicks AND from carousel scroll position.
  function syncActive(panel, idx) {
    idx = String(idx);
    if (panel.__pasActiveIdx === idx) return;
    panel.__pasActiveIdx = idx;

    // Text headings (tablist).
    panel.querySelectorAll('.pas-spec-tabs__feature-tab').forEach((t) => {
      t.setAttribute('aria-selected', t.dataset.pasFeatureIndex === idx ? 'true' : 'false');
    });
    // Thumbnail rail (mirrors the active feature).
    panel.querySelectorAll('.pas-spec-tabs__feature-thumb').forEach((t) => {
      const on = t.dataset.pasFeatureIndex === idx;
      t.classList.toggle('is-active', on);
      if (on) t.setAttribute('aria-current', 'true');
      else t.removeAttribute('aria-current');
    });
    // Descriptions.
    panel.querySelectorAll('[data-pas-feature-description]').forEach((d) => {
      d.toggleAttribute('hidden', d.dataset.pasFeatureDescription !== idx);
    });
    // Arrow controls: counter + disabled state at the ends.
    const count = panel.querySelectorAll('[data-pas-feature-media]').length;
    const current = panel.querySelector('[data-pas-feature-current]');
    if (current) current.textContent = Number(idx) + 1;
    const prev = panel.querySelector('[data-pas-feature-prev]');
    const next = panel.querySelector('[data-pas-feature-next]');
    if (prev) prev.disabled = Number(idx) <= 0;
    if (next) next.disabled = Number(idx) >= count - 1;
    // Slides all stay visible in the scroll carousel; only the active one plays.
    panel.querySelectorAll('[data-pas-feature-media]').forEach((m) => {
      m.classList.toggle('is-active', m.dataset.pasFeatureMedia === idx);
      if (m.dataset.pasFeatureMedia !== idx) pauseMediaIn(m);
      else if (panel.__pasInView !== false) playMediaIn(m);
    });
  }

  // The big-media column is a vertical scroll-snap carousel: scrolling it
  // advances the active feature (page scroll elsewhere is untouched).
  function wireCarousel(scope) {
    (scope || document).querySelectorAll('.pas-spec-tabs__panel-features').forEach((panel) => {
      const slides = panel.querySelector('.pas-spec-tabs__feature-slides');
      if (!slides || slides.__pasCarouselWired) return;
      slides.__pasCarouselWired = true;
      let raf = 0;
      slides.addEventListener(
        'scroll',
        () => {
          if (panel.__pasIgnoreScroll || raf) return;
          raf = requestAnimationFrame(() => {
            raf = 0;
            if (!slides.clientWidth || !slides.clientHeight) return;
            const idx = Math.round(
              slides.scrollLeft / slides.clientWidth + slides.scrollTop / slides.clientHeight
            );
            syncActive(panel, idx);
          });
        },
        { passive: true }
      );
    });
  }

  // --- Ambient video playback ------------------------------------------------

  // Tap-to-play fallback for blocked autoplay (e.g. iOS Low Power Mode, which
  // refuses even muted autoplay). Reveals a play badge that plays the EXISTING
  // <video> on tap — a user gesture always succeeds, and there is no clone /
  // second player, so no white flash. Self-contained; inline-styled.
  function armTapToPlay(video) {
    const host = video.closest('deferred-media') || video.parentElement;
    if (!host || host.__pasTapArmed) return;
    host.__pasTapArmed = true;

    const badge = document.createElement('button');
    badge.type = 'button';
    badge.className = 'pas-tap-to-play';
    badge.setAttribute('aria-label', 'Play video');
    badge.innerHTML =
      '<svg viewBox="0 0 20 20" width="22" height="22" aria-hidden="true" focusable="false">' +
      '<path fill="currentColor" d="M15.375 8.48516C16.5417 9.15874 16.5417 10.8427 15.375 11.5163L7.875 15.8464C6.70833 16.52 5.25 15.678 5.25 14.3308L5.25 5.67058C5.25 4.32343 6.70833 3.48146 7.875 4.15504L15.375 8.48516Z"/></svg>';
    badge.style.cssText =
      'position:absolute;inset:0;margin:auto;width:3.25rem;height:3.25rem;padding:0;' +
      'display:flex;align-items:center;justify-content:center;border:0;border-radius:50%;' +
      'background:rgba(0,0,0,.45);color:#fff;cursor:pointer;z-index:2;';

    const clear = () => {
      host.__pasTapArmed = false;
      video.removeEventListener('playing', clear);
      if (badge.parentNode) badge.parentNode.removeChild(badge);
    };
    badge.addEventListener('click', (e) => {
      e.preventDefault();
      e.stopPropagation();
      video.muted = true;
      video.playsInline = true;
      const p = video.play();
      if (p && typeof p.then === 'function') p.then(clear).catch(() => {});
    });
    // If the video manages to start on its own later, drop the badge.
    video.addEventListener('playing', clear);
    host.appendChild(badge);
  }

  // Before giving up to a tap badge, try to actually get it playing: iOS often
  // won't autoplay a `preload="metadata"` video because no data is buffered yet.
  // So kick a load() and retry play() once the data is ready (mirrors Horizon's
  // video-background-component). Only the still-paused/uninterrupted case.
  function recover(video) {
    if (!video.paused || video.__pasRecovering) return;
    if (video.readyState >= 2) {
      armTapToPlay(video);
      return;
    }
    video.__pasRecovering = true;
    video.preload = 'auto';
    try { video.load(); } catch (e) { /* noop */ }
    let done = false;
    const finish = () => {
      if (done) return;
      done = true;
      video.__pasRecovering = false;
      if (!video.paused) return;
      const p = video.play();
      if (p && typeof p.catch === 'function') p.catch(() => armTapToPlay(video));
      setTimeout(() => { if (video.paused) armTapToPlay(video); }, 700);
    };
    video.addEventListener('canplay', finish, { once: true });
    setTimeout(finish, 2000); // safety if 'canplay' never fires
  }

  function playMediaIn(scope) {
    scope.querySelectorAll('video').forEach((v) => {
      // Guarantee no sound + inline playback (iOS) regardless of markup.
      v.muted = true;
      v.defaultMuted = true;
      v.playsInline = true;
      v.setAttribute('playsinline', '');
      v.preload = 'auto';
      const playing = v.play();
      // Autoplay rejected (e.g. Low Power Mode) → try to recover, then tap-to-play.
      if (playing && typeof playing.catch === 'function') playing.catch(() => recover(v));
      // iOS can also refuse autoplay SILENTLY (no rejection) — the frame just
      // sits frozen. If still paused shortly after, try to recover (load+retry)
      // and ultimately surface the tap-to-play badge so it's never a dead frame.
      setTimeout(() => { if (v.paused) recover(v); }, 800);
    });
  }

  function pauseMediaIn(scope) {
    scope.querySelectorAll('video').forEach((v) => {
      try { v.pause(); } catch (_) { /* noop */ }
    });
    scope.querySelectorAll('iframe').forEach((iframe) => {
      const src = iframe.getAttribute('src');
      if (src) iframe.setAttribute('src', src);
    });
  }

  function activeMedia(panel) {
    const idx = panel.__pasActiveIdx || '0';
    return panel.querySelector('[data-pas-feature-media="' + idx + '"]');
  }

  // Play the active feature's video only while the section is in view; pause it
  // when it scrolls away so offscreen videos don't keep decoding.
  function wireInView(root) {
    const scope = root || document;
    const panels = scope.querySelectorAll('.pas-spec-tabs__panel-features');
    if (!panels.length) return;

    if (!('IntersectionObserver' in window)) {
      panels.forEach((panel) => {
        const m = activeMedia(panel);
        if (m) playMediaIn(m);
      });
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          const panel = entry.target;
          panel.__pasInView = entry.intersectionRatio >= IN_VIEW_THRESHOLD;
          const m = activeMedia(panel);
          if (!m) return;
          if (panel.__pasInView) playMediaIn(m);
          else pauseMediaIn(m);
        });
      },
      { threshold: [0, IN_VIEW_THRESHOLD, 1] }
    );

    panels.forEach((panel) => {
      if (panel.__pasInViewObserved) return;
      panel.__pasInViewObserved = true;
      observer.observe(panel);
    });
  }

  function init() {
    wireInView(document);
    wireCarousel(document);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Theme editor: re-scan when the section is (re)rendered.
  document.addEventListener('shopify:section:load', (event) => {
    wireInView(event.target || document);
    wireCarousel(event.target || document);
  });
})();
